import React, { useState, useEffect } from "react";
import Select from "react-select";
import PhoneNumberInput from "../../components/PhoneInput";
import { useAuth } from "../../contexts/AuthContext";
import APIServices from "../../services/APIServices";
import appToast, { TOAST_TYPE } from "../../utils/AppToast";
import {
  emailLimit,
  getformattedPhoneNumber,
  getPlainPhoneNumber,
  selectLoader,
  userNameLimit,
  validateForm,
} from "../../utils/Helpers";

const UserForm = ({
  roleOptions,
  closeModal,
  orgId = "",
  setAddedUserId,
  adminId,
}) => {
  const { user } = useAuth();
  const [error, setError] = useState();
  const [organizationId, setOrganizationId] = useState("");
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phoneNumber: "",
    userType: null,
    countryDialCode: "+1",
    countryCode: "US",
  });
  const [memberId, setMemberId] = useState("");
  const [loading, setLoading] = useState(false);
  const [roleData, setRoleData] = useState([]);
  const [errors, setErrors] = useState({});
  const [initialData, setInitialData] = useState({});
  const fetchRolesList = async () => {
    // setLoading(true);
    try {
      const response = await APIServices.get(`/user-roles?type=3`); // Adjust the URL as necessary
      if (response.userRoles) {
        const mappedRoles = response.userRoles.map((role) => ({
          value: role.role, // Use 'role' as the value
          label: role.name,
          id: role._id, // Use 'name' as the label
        }));
        setRoleData(mappedRoles);
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (adminId) {
      fetchUserDetails();
    }
  }, [adminId]);

  const fetchUserDetails = async () => {
    setLoading(true);
    try {
      const response = await APIServices.get(`/admin/${adminId}`); // Adjust the URL as necessary
      if (response) {
        setInitialData(response.user);
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (orgId) {
      setOrganizationId(orgId);
    }
  }, [orgId]);

  useEffect(() => {
    if (initialData && adminId) {
      setFormData((prevFormData) => ({
        ...prevFormData,
        fullName: initialData?.fullName || "",
        email: initialData?.email || "",
        phoneNumber: getformattedPhoneNumber(initialData.phoneNumber) || "",
        userType: initialData?.userType?._id,
        countryDialCode: initialData?.countryDialCode || "+1",
        countryCode: initialData?.countryCode || "US",
        userId: initialData?._id,
      }));
      setMemberId(initialData._id);
    }
  }, [initialData]);

  useEffect(() => {
    fetchRolesList();
  }, [roleOptions]);

  //   useEffect(() => {
  //     if (user && !initialData) {
  //       setFormData((prevFormData) => ({
  //         ...prevFormData,
  //         inviterId: user._id,
  //       }));
  //     }
  //   }, [user]);

  // Handle input change and clear errors
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
    if (errors[name]) {
      setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
    }
  };

  const handlePhoneChange = (val, countryDialCode, countryCode) => {
    setFormData({
      ...formData,
      phoneNumber: val,
      countryDialCode: `${countryDialCode}`,
      countryCode: countryCode,
    });

    // Clear the error message for the current field being changed
    setErrors({
      ...errors,
      phoneNumber: "",
    });
  };

  // Handle select change for Role
  const handleSelectChange = (selected) => {
    setFormData((prevData) => ({ ...prevData, userType: selected.id }));
    if (errors.userType) {
      setErrors((prevErrors) => ({ ...prevErrors, userType: "" }));
    }
  };

  // Form Validation
  const validateFormDetails = () => {
    let formErrors = {};

    formErrors = validateForm(formData);

    if (!formData.userType) {
      formErrors.userType = "Please select role.";
    }

    setErrors(formErrors);
    return Object.keys(formErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    const endpoint = memberId ? `/admin/update/${memberId}` : "/admin/register";
    if (validateFormDetails()) {
      setLoading(true);
      // Submit form data
      let plainNumber = getPlainPhoneNumber(formData.phoneNumber);
      const updatedFormValues = {
        ...formData,
        phoneNumber: plainNumber,
        email: formData.email.toLowerCase(),
        // organizationId,
      };
      try {
        const response = memberId
          ? await APIServices.put(endpoint, updatedFormValues)
          : await APIServices.post(endpoint, updatedFormValues);
        if (response.message) {
          appToast(response.message, TOAST_TYPE.SUCCESS);
          if (setAddedUserId) {
            console.log(response?.user?._id);

            setAddedUserId(response?.user?._id);
          }
          closeModal();
        } else {
          appToast(response.data.message, TOAST_TYPE.ERROR);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
      if (!memberId) {
        // Reset form
        // setFormData({
        //   fullName: "",
        //   email: "",
        //   phoneNumber: "",
        //   userType: null,
        //   countryDialCode: "",
        //   countryCode: "US",
        //   inviterId: "",
        // });
        setErrors({});
      }
      console.log(formData);
    }
  };

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <form onSubmit={handleSubmit}>
      <div className="row">
        <div className="col-lg-12">
          <div className="form-group">
            <input
              maxLength={userNameLimit}
              type="text"
              className={`form-control ${
                errors.fullName ? "form-invalid" : ""
              }`}
              name="fullName"
              placeholder="User Name"
              value={formData.fullName}
              onChange={handleInputChange}
            />
            {errors.fullName && (
              <small className="error-message">{errors.fullName}</small>
            )}
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-lg-12">
          <div className="form-group">
            <input
              maxLength={emailLimit}
              type="text"
              className={`form-control ${errors.email ? "form-invalid" : ""}`}
              name="email"
              placeholder="Email Address"
              value={formData.email}
              onChange={handleInputChange}
            />
            {errors.email && (
              <small className="error-message">{errors.email}</small>
            )}
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-lg-12">
          <div className="form-group">
            <PhoneNumberInput
              isDisable={false}
              value={formData.phoneNumber}
              onPhoneChange={(val) =>
                handlePhoneChange(
                  val,
                  formData.countryDialCode,
                  formData.countryCode
                )
              }
              countryDialCode={formData.countryDialCode}
              countryName={formData.countryCode}
              onCountryChange={(country) => {
                setFormData({
                  ...formData,
                  countryDialCode: country.dialCode,
                  countryCode: country.value,
                });
                handlePhoneChange(
                  formData.phoneNumber,
                  country.dialCode,
                  country.value
                );
              }}
              errors={errors.phoneNumber}
            />
            {errors.phoneNumber && (
              <small className="error-message">{errors.phoneNumber}</small>
            )}
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col-lg-12">
          <div className="form-group team-form-group">
            <Select
              className={`form-control select2 form-control-select  ${
                errors.userType ? "form-invalid" : ""
              }`}
              options={roleData}
              value={roleData.find((option) => option.id === formData.userType)}
              onChange={handleSelectChange}
              placeholder="Role"
              styles={{ width: "100%" }}
            />
            {errors.userType && (
              <small className="error-message">{errors.userType}</small>
            )}
          </div>
        </div>
      </div>

      <button className="btn-black">
        {!loading ? (memberId ? "Save" : "Add") : selectLoader(35)}
      </button>
    </form>
  );
};

export default UserForm;
